<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import LabelText from '@/components/forms/LabelText.vue';
import ErrorMessage from '../ErrorMessage.vue';

// Define props
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  labelText: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  parentClass: {
    type: String,
    default: '',
  },
  textareaClass: {
    type: String,
    default: '',
  },
  note: {
    type: String,
    default: '',
  },
  noteClass: {
    type: String,
    default: '',
  },
  size: {
    type: String as () => 'xs' | 'sm' | 'md' | 'lg',
    default: 'md',
  },
  asterisk: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  defaultValue: {
    type: String,
    default: '',
  },
  maxlength: {
    type: [String, Number],
    default: undefined,
  },
  rows: {
    type: [String, Number],
    default: 4,
  },
  // New error-related props
  errorMessage: {
    type: [String, Array] as unknown as () => string | string[],
    default: ''
  },
  errorType: {
    type: String as () => 'error' | 'warning' | 'info',
    default: 'error'
  },
});

// Define size classes
const sizeClasses: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'py-1 px-2 text-xs',
  sm: 'py-2 px-3 text-sm',
  md: 'py-3 px-4 text-base',
  lg: 'py-4 px-5 text-lg',
};

const emit = defineEmits(['update:modelValue']);

// Ensure Default Value Works
const value = ref(props.modelValue || props.defaultValue);

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
  value.value = newValue;
});

// Handle input change
const handleInputChange = (e: Event) => {
  const textarea = e.target as HTMLTextAreaElement;
  value.value = textarea.value;
  emit('update:modelValue', textarea.value);
};

// Simple error check
const hasError = computed(() => {
  return !!props.errorMessage &&
    (typeof props.errorMessage === 'string' ? props.errorMessage.length > 0 : props.errorMessage.length > 0)
});

// Simple dynamic border class
const dynamicTextareaClasses = computed(() => {
  if (hasError.value && props.errorType === 'error') {
    return 'has-error'
  }
  return '';
});
</script>

<template>
  <div :class="['input-wrapper flex flex-col gap-2', parentClass]">
    <LabelText v-if="labelText" :labelText="labelText" :inputId="id" :asterisk="asterisk" />

    <textarea
      :id="id"
      :name="name"
      v-model="value"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInputChange"
      :class="[
        'form-input has-all',
        sizeClasses[props.size],
        textareaClass,
        dynamicTextareaClasses
      ]"
      :aria-labelledby="labelText ? id : undefined"
      :aria-label="labelText ? undefined : (placeholder || name || 'Textarea field')"
      :maxlength="maxlength"
      :rows="rows"
    ></textarea>

    <!-- Note -->
    <p
      v-if="note"
      v-html="note"
      :class="['w-full px-1 text-xs font-light', noteClass]"
    ></p>

    <!-- Error message below textarea -->
    <ErrorMessage
      :message="errorMessage"
      :type="errorType"
    />
  </div>
</template>
